import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { uiWords } from './words';

/**
 * Presentation primitives shared by the admin apps. They hold no business logic and no text of
 * their own beyond `uiWords`: labels come from the caller's dictionary.
 */

export type Tone = 'primary' | 'secondary' | 'danger';

const buttonTones: Record<Tone, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-slate-400',
  secondary:
    'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-100 disabled:text-slate-400',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-slate-400',
};

/** The classes of a button, for links that look like one. */
export function buttonClass(tone: Tone = 'primary'): string {
  return `inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${buttonTones[tone]}`;
}

export function Button({
  tone = 'primary',
  busy = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; busy?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || busy}
      className={`${buttonClass(tone)} ${className}`}
    >
      {busy ? uiWords.loading : children}
    </button>
  );
}

interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

function FieldFrame({
  id,
  label,
  error,
  hint,
  className,
  children,
}: FieldProps & { id: string; className: string; children: ReactNode }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
      {hint === undefined ? null : <div className="text-xs text-slate-500">{hint}</div>}
      {error === undefined ? null : (
        <div id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}

function fieldClass(error: string | undefined): string {
  return `rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-2 focus:outline-brand-600 ${error === undefined ? 'border-slate-300' : 'border-red-500'}`;
}

export function TextField({
  label,
  error,
  hint,
  className = '',
  ...rest
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} error={error} hint={hint} className={className}>
      <input
        id={id}
        {...rest}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        className={fieldClass(error)}
      />
    </FieldFrame>
  );
}

/**
 * A password input with a button that shows what was typed, so a long password can be checked
 * before it is sent. It starts hidden every time the field mounts.
 */
export function PasswordField({
  label,
  error,
  hint,
  className = '',
  ...rest
}: FieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <FieldFrame id={id} label={label} error={error} hint={hint} className={className}>
      <div className="relative">
        <input
          id={id}
          {...rest}
          type={shown ? 'text' : 'password'}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : `${id}-error`}
          className={`w-full pe-10 ${fieldClass(error)}`}
        />
        <button
          type="button"
          aria-label={shown ? uiWords.hidePassword : uiWords.showPassword}
          title={shown ? uiWords.hidePassword : uiWords.showPassword}
          aria-controls={id}
          onClick={() => {
            setShown((value) => !value);
          }}
          className="absolute inset-y-0 end-0 flex w-10 items-center justify-center rounded-e-md text-slate-500 hover:text-slate-800 focus:outline-2 focus:outline-brand-600"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
            {shown ? <path d="M3 3l18 18" /> : null}
          </svg>
        </button>
      </div>
    </FieldFrame>
  );
}

export function TextAreaField({
  label,
  error,
  hint,
  className = '',
  rows = 4,
  ...rest
}: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} error={error} hint={hint} className={className}>
      <textarea
        id={id}
        rows={rows}
        {...rest}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        className={fieldClass(error)}
      />
    </FieldFrame>
  );
}

export function SelectField({
  label,
  error,
  hint,
  children,
  className = '',
  ...rest
}: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} error={error} hint={hint} className={className}>
      <select
        id={id}
        {...rest}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        className={`bg-white ${fieldClass(error)}`}
      >
        {children}
      </select>
    </FieldFrame>
  );
}

/** A labelled checkbox (on/off switches in forms). */
export function CheckboxField({
  label,
  hint,
  className = '',
  ...rest
}: { label: string; hint?: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const id = useId();
  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <input
        id={id}
        type="checkbox"
        {...rest}
        className="mt-0.5 size-4 rounded border-slate-300 accent-brand-600"
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="text-sm font-medium text-slate-700">
          {label}
        </label>
        {hint === undefined ? null : <div className="text-xs text-slate-500">{hint}</div>}
      </div>
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg bg-white p-5 shadow-sm ring-1 ring-slate-200">
      {title === undefined && actions === undefined ? null : (
        <header className="mb-4 flex items-center justify-between gap-4">
          {title === undefined ? <span /> : <h2 className="text-lg font-semibold">{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, actions }: { title: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
      {actions}
    </div>
  );
}

export type BadgeTone = 'green' | 'amber' | 'red' | 'slate' | 'blue';

const badgeTones: Record<BadgeTone, string> = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  slate: 'bg-slate-100 text-slate-700',
  blue: 'bg-brand-100 text-brand-900',
};

export function Badge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeTones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * The badge of an enum value (a status), with a test hook that carries the raw value, e.g.
 * `<span data-testid="order-status" data-status="NEW">`.
 */
export function StatusBadge<Value extends string>({
  value,
  tones,
  labels,
  testId,
}: {
  value: Value;
  tones: Readonly<Record<Value, BadgeTone>>;
  labels: Readonly<Record<Value, string>>;
  testId?: string;
}) {
  return (
    <span data-testid={testId} data-status={value}>
      <Badge tone={tones[value]}>{labels[value]}</Badge>
    </span>
  );
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'info' | 'success' | 'warning';
  children: ReactNode;
}) {
  const styles = {
    error: 'bg-red-50 text-red-800 ring-red-200',
    info: 'bg-brand-50 text-brand-900 ring-brand-100',
    success: 'bg-green-50 text-green-800 ring-green-200',
    warning: 'bg-amber-50 text-amber-900 ring-amber-200',
  }[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-md p-3 text-sm ring-1 ${styles}`}
    >
      {children}
    </div>
  );
}

/** A small square preview of an image. */
export function Thumbnail({ src, alt, size = 64 }: { src: string; alt: string; size?: number }) {
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      loading="lazy"
      className="rounded bg-slate-100 object-contain ring-1 ring-slate-200"
      style={{ width: size, height: size }}
    />
  );
}

export function Loading() {
  return <p className="text-sm text-slate-500">{uiWords.loading}</p>;
}

/** A value to copy (an invitation link, a secret), selectable and with a copy button. */
export function CopyField({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          readOnly
          value={value}
          data-testid={testId}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          className="ltr flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs"
        />
        <Button
          tone="secondary"
          onClick={() => {
            // The clipboard may be unavailable or refused (permissions, plain HTTP): then say so,
            // and the value stays selectable in the field.
            Promise.resolve()
              .then(() => navigator.clipboard.writeText(value))
              .then(
                () => {
                  setCopied('yes');
                },
                () => {
                  setCopied('failed');
                },
              );
          }}
        >
          {copied === 'yes' ? uiWords.copied : uiWords.copy}
        </Button>
      </div>
      {copied === 'failed' ? (
        <div role="alert" className="text-xs text-red-600">
          {uiWords.copyFailed}
        </div>
      ) : null}
    </div>
  );
}

/** A modal confirmation built on the native <dialog> (focus trap and Escape for free). */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = 'primary',
  confirmTestId,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: Tone;
  /** The test hook of the confirm button. */
  confirmTestId?: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(event) => {
        // Escape does not close the dialog while its action runs.
        if (busy) event.preventDefault();
      }}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-slate-900/40"
    >
      <div className="flex flex-col gap-4 p-6" dir="rtl">
        <h2 id={titleId} className="text-lg font-semibold">
          {title}
        </h2>
        <div className="text-sm text-slate-700">{children}</div>
        <div className="flex justify-start gap-2">
          <Button tone={tone} busy={busy} onClick={onConfirm} data-testid={confirmTestId}>
            {confirmLabel}
          </Button>
          <Button tone="secondary" onClick={onClose} disabled={busy}>
            {uiWords.cancel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

export function Table({
  head,
  lead,
  children,
}: {
  head: readonly string[];
  /** What the first column's heading holds when it is not a label (a "select all" checkbox). */
  lead?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-right text-sm">
        <thead className="border-b border-slate-200 text-xs text-slate-500">
          <tr>
            {lead === undefined ? null : <th className="w-8 px-3 py-2 font-medium">{lead}</th>}
            {head.map((label, index) => (
              <th key={`${String(index)}-${label}`} className="px-3 py-2 font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Cell({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

/** "Load more" under a cursor-paged list; renders nothing on the last page. */
export function LoadMore({
  hasMore,
  busy,
  onMore,
}: {
  hasMore: boolean;
  busy: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="mt-4">
      <Button tone="secondary" busy={busy} onClick={onMore}>
        {uiWords.loadMore}
      </Button>
    </div>
  );
}
